import { useState, useEffect } from "react";
import api from "@/services/api";
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { prefixer } from "stylis";
import "./styles.css";
import authStore from "@/stores/authStore";
import OrdersFilter from "@/components/orders/ordersFilter";
import OrdersTable from "@/components/orders/ordersTable";
import popupService from "@/services/popupService";
import { OrderSearchItem, OrdersFilterResponse, OrdersFilterRequest } from "@/models/orders";
import { PermissionName } from "@/models/apiModels";
import { checkPermissionsWithAlert, formatDate } from "@/util";
import FilterCancelIcon from "../styled/filter.cancel.icon";
import { useTranslation } from "react-i18next";
import filtersStore, { OrdersFilterData } from "@/stores/filtersStore";
import TotalCountComponent from "@/components/common/resultCountComponent";
import { Dayjs } from "dayjs";

const cacheLtr = createCache({
    key: "muiltr",
});

const cacheRtl = createCache({
    key: "muirtl",
    stylisPlugins: [prefixer, rtlPlugin],
});

const Orders = () => {
    const { t } = useTranslation();
    const [orders, setOrders] = useState<OrderSearchItem[]>([]);
    const [total, setTotal] = useState(0);
    const [totalFilter, setTotalFilter] = useState(0);
    const [ordersFilter, setOrdersFilter] = useState<OrdersFilterRequest>(new OrdersFilterRequest());
    const [searchCounter, setSearchCounter] = useState<number>(0);
    const [filterTabsCounter, setFilterTabsCounter] = useState<number>(0);
    const [showOrdersTable, setShowOrdersTable] = useState(true);
    const [filterTabsArray, setFilterTabsArray] = useState<Array<{ id: string; name: string; value: string | null }>>(
        []
    );
    const [arrivalDateStart, setArrivalDateStart] = useState<Dayjs | null>(null);
    const [arrivalDateEnd, setArrivalDateEnd] = useState<Dayjs | null>(null);
    const [departureDateStart, setDepartureDateStart] = useState<Dayjs | null>(null);
    const [departureDateEnd, setDepartureDateEnd] = useState<Dayjs | null>(null);

    const filterTabs = (
        <div
            id="filter-tabs"
            className="accounting-filter-tab-container"
            style={filterTabsArray.length > 0 ? { marginTop: "10px" } : { marginTop: "0px" }}
        >
            {filterTabsArray.map((filterTab) => (
                <div key={filterTab.id} className="filter-tab-element font-style">
                    <span>
                        <span className="tab-color">{filterTab.name}</span> {filterTab.value}
                    </span>
                    <button className="filter-tab-button" onClick={() => removeFilterTab(filterTab.name, filterTab.id)}>
                        <FilterCancelIcon />
                    </button>
                </div>
            ))}
        </div>
    );

    const setFilterTabs = async () => {
        const filters = [
            {
                id: "keyword-input",
                name: ordersFilter.keywords,
                value: `${t("employee.QuickSearch")}:`,
            },
            {
                id: "from-date-input",
                name: ordersFilter.fromDate != null ? formatDate(ordersFilter.fromDate.toString()) : null,
                value: `${t("orders.fromDate")}:`,
            },
            {
                id: "to-date-input",
                name: ordersFilter.toDate != null ? formatDate(ordersFilter.toDate.toString()) : null,
                value: `${t("orders.endDate")}:`,
            },
            {
                id: "order-status-input",
                name: ordersFilter.orderStatus?.toString() ?? "",
                value: `${t("orders.orderStatus")}:`,
            },
            {
                id: "order-type-input",
                name: ordersFilter.orderType?.toString() ?? "",
                value: `${t("orders.orderType")}:`,
            },
            {
                id: "employee-status-input",
                name: ordersFilter.employeeStatus?.toString() ?? "",
                value: `${t("employee.FilterStatus")}:`,
            },
            {
                id: "arrival-date-start-input",
                name: ordersFilter.arrivalDateStart != null ? ordersFilter.arrivalDateStart : null,
                value: `${t("general.ArrivalStart")}:`,
            },
            {
                id: "arrival-date-end-input",
                name: ordersFilter.arrivalDateEnd != null ? ordersFilter.arrivalDateEnd : null,
                value: `${t("general.ArrivalEnd")}:`,
            },
            {
                id: "departure-date-start-input",
                name: ordersFilter.departureDateStart != null ? ordersFilter.departureDateStart : null,
                value: `${t("general.DepartureStart")}:`,
            },
            {
                id: "departure-date-end-input",
                name: ordersFilter.departureDateEnd != null ? ordersFilter.departureDateEnd : null,
                value: `${t("general.DepartureEnd")}:`,
            },
        ];

        const filteredTabs = filters
            .filter((filter) => filter.name !== null && filter.name !== "")
            .map((filter) => ({ id: filter.id, name: filter.value, value: filter.name }));

        setFilterTabsArray(filteredTabs);
    };

    const removeFilterTab = (tabName: string | undefined, tabId: string) => {
        const newArray = [...filterTabsArray];
        const index = newArray.findIndex((tab) => tab.name === tabName);
        newArray.splice(index, 1);
        setFilterTabsArray(newArray);

        switch (tabId) {
            case "keyword-input":
                setOrdersFilter(() => ({
                    ...ordersFilter,
                    keywords: "",
                }));
                break;
            case "from-date-input":
                setOrdersFilter(() => ({
                    ...ordersFilter,
                    fromDate: null,
                }));
                break;
            case "to-date-input":
                setOrdersFilter(() => ({
                    ...ordersFilter,
                    toDate: null,
                }));
                break;
            case "order-status-input":
                setOrdersFilter(() => ({
                    ...ordersFilter,
                    orderStatus: null,
                }));
                break;
            case "order-type-input":
                setOrdersFilter(() => ({
                    ...ordersFilter,
                    orderType: null,
                }));
                break;
            case "employee-status-input":
                setOrdersFilter(() => ({
                    ...ordersFilter,
                    employeeStatus: null,
                }));
                break;
            case "arrival-date-start-input":
                setOrdersFilter((prevFilter) => ({
                    ...prevFilter,
                    arrivalDateStart: null,
                }));
                setArrivalDateStart(null);
                break;
            case "arrival-date-end-input":
                setOrdersFilter((prevFilter) => ({
                    ...prevFilter,
                    arrivalDateEnd: null,
                }));
                setArrivalDateEnd(null);
                break;
            case "departure-date-start-input":
                setOrdersFilter((prevFilter) => ({
                    ...prevFilter,
                    departureDateStart: null,
                }));
                setDepartureDateStart(null);
                break;
            case "departure-date-end-input":
                setOrdersFilter((prevFilter) => ({
                    ...prevFilter,
                    departureDateEnd: null,
                }));
                setDepartureDateEnd(null);
                break;
            default:
                break;
        }
    };

    const onSearchClick = async () => {
        setOrders([]);
        filtersStore.setOrdersFilterData(null);
        await setShowOrdersTable(false);
        setPageIndex(0);
        setShowOrdersTable(true);
        setSearchCounter(searchCounter + 1);
    };

    const setPageIndex = (index?: number) => {
        setOrdersFilter((prevData) => ({
            ...prevData,
            pageIndex: index ?? ordersFilter.pageIndex + 1,
        }));
    };

    async function getOrders(): Promise<OrdersFilterResponse> {
        const filterStore = filtersStore.ordersFilterData;
        if (filterStore != null) {
            if (filterStore.filter?.pageIndex !== 0) {
                if (filterStore.filter) filterStore.filter.pageIndex = 0;
            }
            setOrdersFilter(filterStore.filter ?? new OrdersFilterRequest());
            setFilterTabsArray(filterStore.filterTabs);
            const responsePromise = api.getOrders(filterStore.filter ?? new OrdersFilterRequest());
            return (await responsePromise).data;
        }

        const ordersFilterData = new OrdersFilterData();
        ordersFilterData.filter = ordersFilter;
        ordersFilterData.filterTabs = filterTabsArray;
        filtersStore.setOrdersFilterData(ordersFilterData);

        const response = await api.getOrders(ordersFilter);
        return response.data;
    }

    let startFetchingBusy = false;
    const startFetching = async () => {
        if (!checkPermissionsWithAlert(PermissionName.ListOfOrders)) {
            return;
        }
        if (startFetchingBusy) {
            return;
        }
        try {
            startFetchingBusy = true;
            popupService.showLoading();
            const response = await getOrders();
            if (ordersFilter.pageIndex === 0) {
                setTotal(response.total);
                setTotalFilter(response.totalFilter);
                setOrders(response.orders);
            } else {
                setOrders((oldArray) => [...oldArray, ...response.orders]);
            }
            setFilterTabsCounter(filterTabsCounter + 1);
            popupService.close();
        } catch (err) {
            popupService.showApiError(err);
            return;
        } finally {
            startFetchingBusy = false;
        }
    };

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            startFetching();
        }
        return () => {
            ignore = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ordersFilter.pageIndex, ordersFilter.orderBy, ordersFilter.orderAsc, searchCounter]);

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            const fetchFilterTabs = async () => {
                await setFilterTabs();
            };
            fetchFilterTabs();
        }
        return () => {
            ignore = true;
        };
    }, [filterTabsCounter]);

    return (
        <CacheProvider value={authStore.isRtl ? cacheRtl : cacheLtr}>
            {checkPermissionsWithAlert(PermissionName.ListOfOrders) ? (
                <div className="order-container">
                    <OrdersFilter
                        searchClick={onSearchClick}
                        total={total}
                        filter={ordersFilter}
                        setFilter={setOrdersFilter}
                        filterTabs={filterTabs}
                        arrivalDateStart={arrivalDateStart}
                        arrivalDateEnd={arrivalDateEnd}
                        departureDateStart={departureDateStart}
                        departureDateEnd={departureDateEnd}
                        setArrivalDateStart={setArrivalDateStart}
                        setArrivalDateEnd={setArrivalDateEnd}
                        setDepartureDateStart={setDepartureDateStart}
                        setDepartureDateEnd={setDepartureDateEnd}
                    />
                    <TotalCountComponent
                        title={t("employees.QuantityOfOrders") as string}
                        allCount={total}
                        currentCount={totalFilter}
                    />
                    {showOrdersTable ? (
                        <OrdersTable
                            orders={orders}
                            filter={ordersFilter}
                            setFilter={setOrdersFilter}
                            total={total}
                            fetchMore={setPageIndex}
                        />
                    ) : (
                        ""
                    )}
                </div>
            ) : (
                ""
            )}
        </CacheProvider>
    );
};

export default Orders;
